import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box, Card, CardContent, Typography, TextField, Button, Switch, Checkbox,
  FormControlLabel, Alert, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";

import api from "./../api";
import { PageHeader } from "./../components/ui";

// One scope category: an "all X" checkbox, and per-item checkboxes when not-all.
function ScopeField({ label, items, all, onAll, sel, onToggle }) {
  return (
    <Box>
      <FormControlLabel
        control={<Checkbox checked={all} onChange={(e) => onAll(e.target.checked)} size="small" />}
        label={`all ${label}`}
      />
      {!all && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", pl: 1.5, pb: 0.5 }}>
          {items.length === 0 && (
            <Typography variant="caption" color="text.secondary">No {label} available.</Typography>
          )}
          {items.map((it) => (
            <FormControlLabel
              key={it.name}
              control={
                <Checkbox
                  size="small"
                  checked={sel.includes(it.name)}
                  onChange={(e) => onToggle(it.name, e.target.checked)}
                />
              }
              label={it.name}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

const ALL_ON = { gen: true, clf: true, llm: true };
const SEL_EMPTY = { gen: [], clf: [], llm: [] };

export default function Tokens() {
  const [tokens, setTokens] = useState([]);
  const [gens, setGens] = useState([]);
  const [clfs, setClfs] = useState([]);
  const [llms, setLlms] = useState([]);
  const [created, setCreated] = useState(null);
  const [name, setName] = useState("");
  // create scopes
  const [cAll, setCAll] = useState(ALL_ON);
  const [cSel, setCSel] = useState(SEL_EMPTY);
  // edit scopes (editing = the token being edited, or null)
  const [editing, setEditing] = useState(null);
  const [eAll, setEAll] = useState(ALL_ON);
  const [eSel, setESel] = useState(SEL_EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/api/tokens", { silent: true }).then((d) => setTokens(d.tokens)).catch(() => {});
    api.get("/api/generators", { silent: true }).then((d) => setGens(d.generators || [])).catch(() => {});
    api.get("/api/classifiers", { silent: true }).then((d) => setClfs(d.classifiers || [])).catch(() => {});
    api.get("/api/vllm-models", { silent: true }).then((d) => setLlms(d.models || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleIn = (setter, cat, val, checked) =>
    setter((s) => ({ ...s, [cat]: checked ? [...s[cat], val] : s[cat].filter((x) => x !== val) }));
  const setAllIn = (setter, cat, val) => setter((s) => ({ ...s, [cat]: val }));

  // {all,sel} → the list the API wants (null = all, else the selected names).
  const scoped = (all, sel, cat) => (all[cat] ? null : sel[cat]);

  const create = async () => {
    try {
      const r = await api.post("/api/tokens", {
        name,
        allowed_generators: scoped(cAll, cSel, "gen"),
        allowed_classifiers: scoped(cAll, cSel, "clf"),
        allowed_vllm: scoped(cAll, cSel, "llm"),
      });
      setCreated(r.token);
      setName(""); setCAll(ALL_ON); setCSel(SEL_EMPTY);
      load();
    } catch { /* api wrapper toasts */ }
  };

  const del = async (id) => {
    if (!window.confirm("Delete token?")) return;
    await api.del(`/api/tokens/${id}`);
    toast.success("Token deleted");
    load();
  };

  const toggle = async (t) => {
    await api.patch(`/api/tokens/${t.id}`, { enabled: !t.enabled });
    load();
  };

  const openEditScope = (t) => {
    setEditing(t);
    setEAll({ gen: !t.allowed_generators, clf: !t.allowed_classifiers, llm: !t.allowed_vllm });
    setESel({
      gen: t.allowed_generators || [], clf: t.allowed_classifiers || [], llm: t.allowed_vllm || [],
    });
  };

  const saveScope = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/tokens/${editing.id}`, {
        allowed_generators: scoped(eAll, eSel, "gen"),
        allowed_classifiers: scoped(eAll, eSel, "clf"),
        allowed_vllm: scoped(eAll, eSel, "llm"),
      });
      toast.success("Scope updated");
      setEditing(null);
      load();
    } catch { /* api wrapper toasts */ } finally {
      setSaving(false);
    }
  };

  const summary = (list) => (list ? (list.length ? list.join(", ") : "none") : "all");

  const fields = (all, setAll, sel, setSel) => (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      <ScopeField label="generators" items={gens} all={all.gen}
        onAll={(v) => setAllIn(setAll, "gen", v)} sel={sel.gen}
        onToggle={(n, c) => toggleIn(setSel, "gen", n, c)} />
      <ScopeField label="classifiers" items={clfs} all={all.clf}
        onAll={(v) => setAllIn(setAll, "clf", v)} sel={sel.clf}
        onToggle={(n, c) => toggleIn(setSel, "clf", n, c)} />
      <ScopeField label="LLMs" items={llms} all={all.llm}
        onAll={(v) => setAllIn(setAll, "llm", v)} sel={sel.llm}
        onToggle={(n, c) => toggleIn(setSel, "llm", n, c)} />
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        eyebrow="Access"
        title="API tokens"
        subtitle="Issue bearer tokens for AI Factory instances. Each token can be scoped to specific generators, classifiers and LLMs, and shows once at creation."
      />

      <Card sx={{ mb: 1.75 }}>
        <CardContent>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Create a token for an AI Factory instance
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
            <TextField
              size="small" placeholder="name (e.g. prod-factory)"
              value={name} onChange={(e) => setName(e.target.value)}
            />
            <Button variant="contained" disabled={!name} onClick={create}>Create</Button>
          </Stack>

          {fields(cAll, setCAll, cSel, setCSel)}

          {created && (
            <Alert severity="info" sx={{ mt: 1.5 }} onClose={() => setCreated(null)}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                Copy now — shown once
              </Typography>
              <Box sx={{ fontFamily: "monospace", wordBreak: "break-all", mt: 0.5 }}>{created}</Box>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Prefix</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((t) => (
                <TableRow key={t.id}>
                  <TableCell sx={{ fontWeight: 700 }}>{t.name}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{t.key_prefix}…</TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: ".78rem", lineHeight: 1.5 }}>
                    <div>gen: {summary(t.allowed_generators)}</div>
                    <div>clf: {summary(t.allowed_classifiers)}</div>
                    <div>llm: {summary(t.allowed_vllm)}</div>
                  </TableCell>
                  <TableCell>
                    <Switch size="small" checked={t.enabled} onChange={() => toggle(t)} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit scope">
                      <IconButton size="small" onClick={() => openEditScope(t)}>
                        <TuneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete token">
                      <IconButton size="small" color="error" onClick={() => del(t.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit scope
          {editing && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {editing.name} · {editing.key_prefix}…
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {fields(eAll, setEAll, eSel, setESel)}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveScope} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
